import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { brandingService, BrandingSettings } from '@/services/brandingService';

interface BrandingContextValue {
  branding: BrandingSettings;
  isLoading: boolean;
  refreshBranding: () => Promise<void>;
}

const BRANDING_STORAGE_KEY = 'branding-settings';

const defaultBranding: BrandingSettings = {
  appName: '',
  appSubtitle: 'Admin Dashboard',
  modules: {
    categories: false,
    tvChannels: false,
    liveTvCountriesEnabled: false,
    radioCountriesEnabled: false,
    tvGuide: false,
    radioStations: false,
    documentaries: false,
    movies: false,
    concerts: false,
    tvShows: false,
    sports: false,
    iptvProviders: false,
    flussonicServers: false,
    channelAutomations: false,
    channelScraping: false,
    fullAddressEnabled: false,
    reports: false,
    accounting: false,
  },
};

const getBrandingStorageKey = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return `${BRANDING_STORAGE_KEY}:${window.location.host || 'default'}`;
};

const readCachedBranding = (): BrandingSettings | null => {
  const storageKey = getBrandingStorageKey();
  if (!storageKey) {
    return null;
  }

  const stored = localStorage.getItem(storageKey);
  if (!stored) {
    return null;
  }

  try {
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return parsed as BrandingSettings;
  } catch (error) {
    return null;
  }
};

const writeCachedBranding = (branding: BrandingSettings) => {
  const storageKey = getBrandingStorageKey();
  if (!storageKey) {
    return;
  }

  localStorage.setItem(storageKey, JSON.stringify(branding));
};

const areBrandingsEqual = (left: BrandingSettings, right: BrandingSettings) =>
  JSON.stringify(left) === JSON.stringify(right);

const BrandingContext = createContext<BrandingContextValue>({
  branding: defaultBranding,
  isLoading: true,
  refreshBranding: async () => {},
});

const applyBrandingToDocument = (branding: BrandingSettings) => {
  const root = document.documentElement;

  if (branding.primaryColor) {
    root.style.setProperty('--brand-primary', branding.primaryColor);
    root.style.setProperty('--brand-primary-hover', branding.primaryColor);
  }
  if (branding.secondaryColor) {
    root.style.setProperty('--brand-secondary', branding.secondaryColor);
  }
  if (branding.accentColor) {
    root.style.setProperty('--brand-accent', branding.accentColor);
  }
  if (branding.fontFamily) {
    root.style.setProperty('--brand-font', branding.fontFamily);
  }

  if (branding.appName) {
    document.title = branding.appName;
  }

  if (branding.faviconUrl) {
    const favicon = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (favicon) {
      favicon.href = branding.faviconUrl;
    }
  }
};

export const BrandingProvider = ({ children }: { children: ReactNode }) => {
  const cachedBranding = readCachedBranding();
  const initialBranding = cachedBranding ? { ...defaultBranding, ...cachedBranding } : defaultBranding;
  const [branding, setBranding] = useState<BrandingSettings>(initialBranding);
  const [isLoading, setIsLoading] = useState(!cachedBranding);

  useEffect(() => {
    applyBrandingToDocument(branding);
  }, [branding]);

  useEffect(() => {
    const loadBranding = async () => {
      const response = await brandingService.getBranding();
      if (!response) {
        setIsLoading(false);
        return;
      }

      const nextBranding = { ...defaultBranding, ...(response || {}) };
      setBranding((current) => {
        if (areBrandingsEqual(current, nextBranding)) {
          return current;
        }
        return nextBranding;
      });
      if (!areBrandingsEqual(initialBranding, nextBranding)) {
        writeCachedBranding(nextBranding);
      }
      setIsLoading(false);
    };

    loadBranding();
  }, []);

  const value = useMemo(() => ({
    branding,
    isLoading,
    refreshBranding: async () => {
      const response = await brandingService.getBranding();
      if (response) {
        const nextBranding = { ...defaultBranding, ...response };
        setBranding(nextBranding);
        writeCachedBranding(nextBranding);
      }
    },
  }), [branding, isLoading]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
